import type {
  BuyerRespondMutationOptions,
  BuyerRespondVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { buyerRespond } from './buyerRespond';

/**
 * Mutation key for buyer respond actions.
 * Used with useMutationState to track pending buyer actions.
 */
export const MUTATION_KEY_BUYER_RESPOND = ['calloffs', 'buyerRespond'] as const;

/**
 * Custom hook for submitting a buyer action response for a call-off.
 * Performs state transitions based on the selected action:
 * - confirm: Accept the call-off as-is
 * - confirm_with_changes: Accept with proposed modifications
 * - decline: Reject the call-off
 * - withdraw: Cancel/withdraw the call-off
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link/operation/buyer_respond_calloffs__calloff_id__magic_link_respond_post
 */
export const useMutationBuyerRespond = (
  options?: BuyerRespondMutationOptions,
) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    mutationFn: async ({ callOffId, payload }: BuyerRespondVariables) =>
      buyerRespond(callOffId, payload),
    mutationKey: MUTATION_KEY_BUYER_RESPOND,
    onSuccess: async (data, variables, context) => {
      // Invalidate the call-off detail query
      await queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(variables.callOffId),
      });

      // Invalidate the call-offs list query
      await queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getAll(undefined),
      });

      // Invalidate the buyer actions query to refresh available actions
      await queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getBuyerActions(variables.callOffId),
      });

      // Invalidate the call-off history query to refresh history
      await queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getHistory(variables.callOffId),
      });

      // Call the user-provided onSuccess callback if any
      return onSuccess?.(data, variables, context);
    },
    ...rest,
  });
};
